// CloseIssueDialog — the modal that closes an issue. Closing is a destination,
// not a verdict: an issue can reach Closed from any non-terminal state, so the
// dialog requires an OUTCOME reason (resolved / superseded / withdrawn /
// unresolved-on-record) plus an optional note. The outcome is surfaced later
// as a drawer-header sub-label and in the PDF, so the audit trail records WHY
// the issue ended, not just that it did. The Close button stays disabled until
// an outcome is chosen so a close is never recorded without its reason.

import { useState } from "react";
import Modal from "../../../../common/Modal";
import type { IssueCloseCommand, IssueCloseOutcome } from "../../../../../types";

export interface CloseIssueDialogProps {
  onClose: () => void;
  onConfirm: (input: IssueCloseCommand) => void;
  isSubmitting: boolean;
}

const OUTCOME_OPTIONS: {
  value: IssueCloseOutcome;
  label: string;
  hint: string;
}[] = [
  {
    value: "resolved",
    label: "Resolved",
    hint: "The issue was fixed and confirmed.",
  },
  {
    value: "superseded",
    label: "Superseded",
    hint: "A variation or another issue subsumes this one.",
  },
  {
    value: "withdrawn",
    label: "Withdrawn",
    hint: "Raised in error or no longer applies.",
  },
  {
    value: "unresolved_on_record",
    label: "Unresolved (on record)",
    hint: "The contract ended or the issue went stale; kept for the record.",
  },
];

export default function CloseIssueDialog({
  onClose,
  onConfirm,
  isSubmitting,
}: CloseIssueDialogProps) {
  const [outcome, setOutcome] = useState<IssueCloseOutcome | "">("");
  const [note, setNote] = useState("");

  const handleConfirm = () => {
    if (!outcome) return;
    const trimmed = note.trim();
    onConfirm({
      closeOutcome: outcome,
      ...(trimmed ? { outcomeNote: trimmed } : {}),
    });
  };

  return (
    <Modal isOpen onClose={onClose} title="Close issue">
      <div className="space-y-4">
        <fieldset className="space-y-2">
          <legend className="label">Outcome</legend>
          {OUTCOME_OPTIONS.map((opt) => (
            <label
              key={opt.value}
              htmlFor={`close-outcome-${opt.value}`}
              className="flex cursor-pointer items-start gap-2 rounded-md border border-slate-200 p-2 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-700/40"
            >
              <input
                id={`close-outcome-${opt.value}`}
                type="radio"
                name="close-outcome"
                value={opt.value}
                checked={outcome === opt.value}
                onChange={() => setOutcome(opt.value)}
                className="mt-0.5 accent-brand-600"
              />
              <span>
                <span className="block text-body-sm font-medium text-slate-800 dark:text-slate-100">
                  {opt.label}
                </span>
                <span className="block text-caption text-slate-500 dark:text-slate-400">
                  {opt.hint}
                </span>
              </span>
            </label>
          ))}
        </fieldset>

        <div>
          <label htmlFor="close-note" className="label">
            Note (optional)
          </label>
          <textarea
            id="close-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            className="input w-full resize-y"
            placeholder="Add context for the record…"
          />
        </div>

        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!outcome || isSubmitting}
            className="btn-primary"
          >
            Close issue
          </button>
        </div>
      </div>
    </Modal>
  );
}
